"use client";
import React, { useEffect, useMemo, useState } from "react";
import Card from "@/assets/AntDesign/Card";
import Button from "@/assets/AntDesign/Button";
import Typography from "@/assets/AntDesign/Typography";
import { useTranslation } from "react-i18next";

import EmergencyModal from "../../../../navigation/Emergencymodal/Emergencymodal";
import "./EmergencyContact.css";

// ✅ Hook branché sur thunks/slice Redux
import useEmergencyContact from "@/Profile/hooks/useEmergencyContact";

const { Text } = Typography;

function ShieldIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3.2l7 2.6v6.2c0 4.2-2.8 7.9-7 9-4.2-1.1-7-4.8-7-9V5.8l7-2.6z" />
      <path d="M9.5 12l1.7 1.7 3.3-3.3" />
    </svg>
  );
}

export default function EmergencyContact() {
  const { t } = useTranslation();
  const [isEmergencyOpen, setIsEmergencyOpen] = useState(false);
  const {
    userId,
    contacts,
    status,
    error,
    handleRefresh,
    handleCall,
  } = useEmergencyContact();

  // ✅ Refresh au montage
  useEffect(() => {
    if (!userId) return;
    handleRefresh();
  }, [userId, handleRefresh]);

  const isLoading = status === "loading";

  // ✅ Normaliser contacts
  const items = useMemo(() => {
    return Array.isArray(contacts) ? contacts : [];
  }, [contacts]);

  return (
    <>
      <Card
        className="ec-card"
        style={{
          background: "#FFEFEF",
          border: "1px solid #F8D6D6",
          borderRadius: 16,
          boxShadow: "0 6px 18px rgba(217,43,43,.06)",
          padding: 12,
          height: "auto",
        }}
      >
        <div className="flex-display">
          <div className="ec-card__icon--container">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M15 9.74985C15 13.4998 12.375 15.3748 9.255 16.4623C9.09162 16.5177 8.91415 16.5151 8.7525 16.4548C5.625 15.3748 3 13.4998 3 9.74985V4.49985C3 4.30094 3.07902 4.11017 3.21967 3.96952C3.36032 3.82887 3.55109 3.74985 3.75 3.74985C5.25 3.74985 7.125 2.84985 8.43 1.70985C8.58889 1.5741 8.79102 1.49951 9 1.49951C9.20898 1.49951 9.41111 1.5741 9.57 1.70985C10.8825 2.85735 12.75 3.74985 14.25 3.74985C14.4489 3.74985 14.6397 3.82887 14.7803 3.96952C14.921 4.11017 15 4.30094 15 4.49985V9.74985Z" stroke="#FFEAEA" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 6V9" stroke="#FFEAEA" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 12H9.0075" stroke="#FFEAEA" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div className="ec-title" style={{ width: "310px" }}>
            <label>{t("emergencyContact.title")}</label>
          </div>
        </div>

        {/* Loading */}
        {isLoading && (
          <Text type="secondary">
            {t("emergencyContact.loading")}
          </Text>
        )}

        {/* Error */}
        {!isLoading && error && (
          <Text type="danger">
            {String(error)}
          </Text>
        )}

        {/* Empty */}
        {!isLoading && !error && items.length === 0 && (
          <Text type="secondary">
            {t("emergencyContact.empty")}
          </Text>
        )}

        {/* List */}
        {!isLoading && !error && items.length > 0 && (
          <div className="ec-list">
            {items.map((c) => {
              const key = c?._id ?? c?.id;
              const name = c?.name ?? t("emergencyContact.unknown");
              const relation = c?.relation
                ? ` (${t("emergencyContact.relation", { relation: c.relation })})`
                : "";
              const phone = c?.phone;

              return (
                <div key={key} className="ec-person">
                  <div className="ec-name">
                    {name}
                    {relation}
                  </div>

                  {phone && (
                    <Button
                      type="link"
                      className="ec-phone"
                      onClick={() => handleCall(phone)}
                    >
                      {phone}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          className="floating-btn--emergency emergency-contact-trigger"
          onClick={() => setIsEmergencyOpen(true)}
          aria-label={t("emergencyContact.open", "Open emergency contacts")}
        >
          <span>{t("emergencyContact.open", "Open")}</span>
        </button>
      </Card>

      <EmergencyModal
        isOpen={isEmergencyOpen}
        onClose={() => setIsEmergencyOpen(false)}
      />
    </>
  );
}
